/* eslint-disable react/prop-types */
import { buildPcModelAttrs } from 'helpers/buildPcModelAttrs';
import { deconstructDoc } from 'helpers/deconstructDoc';
import PropTypes from 'prop-types';
import { flatten, groupBy, isEmpty, map, mergeAll, values } from 'ramda';
import { createContext, useContext, useEffect, useState } from 'react';
import { JsonParam, useQueryParam, withDefault } from 'use-query-params';
import { debounce } from 'lodash'; // added import for debounce

const PcConfiguratorContext = createContext(null);

export const PcConfiguratorProvider = ({
  pcModel,
  prices,
  stocks,
  pcConfigDoc,
  children,
}) => {
  const { slug } = pcModel;
  const [options, setOptions] = useState([]);
  const [baseOptions, setBaseOptions] = useState([]);
  const [baseSelections, setBaseSelections] = useState({});
  const [currentOptions, setCurrentOptions] = useState([]);
  const [currentSelections, setCurrentSelections] = useState({});
  const [subGroups, setSubGroups] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  const [selections] = useQueryParam('selections', withDefault(JsonParam, {}));

  const getBaseSelections = (_options = []) =>
    _options.reduce((_baseSelections, option) => {
      if (option.is_base) {
        return { ..._baseSelections, [option.group_type]: option.variant_id };
      }
      return _baseSelections;
    }, {});

  const attachPrices = (_options = [], _prices) =>
    _options.map((option) => {
      const price = _prices.find(
        (_price) =>
          _price.group_type === option.group_type &&
          _price.variant_id === option.variant_id,
      );

      return {
        ...option,
        price_diff: price?.price_diff,
        compared_at_price_diff: price?.compared_at_price_diff,
      };
    });

  const attachStock = (_options = [], _stocks) =>
    _options.map((option) => {
      const stock = _stocks.find(
        (_stock) => option.variant_id === _stock.variant_id,
      );
      if (stock?.variant_id === 45224775843995) {
        return {
          ...option,
          in_stock: true,
          tracked: false,
        };
      }
      return {
        ...option,
        in_stock:
          !stock ||
          !stock.tracked ||
          stock.policy === 'CONTINUE' ||
          stock.quantity > 0,
      };
    });

  const sortByPriceWithinGroupTypes = (opts) => {
    const sortByPrice = (a, b) => {
      const aPrice = a.compared_at_price_diff || a.price_diff || 0;
      const bPrice = b.compared_at_price_diff || b.price_diff || 0;
      return aPrice - bPrice;
    };

    const groupByGroupType = groupBy((option) => option.group_type);
    const optionsByGroupType = groupByGroupType(opts);

    const sortedOptionsByGroupType = map(
      (groupTypeOpts) => groupTypeOpts.sort(sortByPrice),
      optionsByGroupType,
    );

    return flatten(values(sortedOptionsByGroupType));
  };

  useEffect(() => {
    if (pcModel) {
      const { options: newOptions, sub_groups: newSubgroups } =
        deconstructDoc(pcModel);
      const newBaseSelections = getBaseSelections(newOptions);
      setBaseSelections(newBaseSelections);

      const optionsWithPrices = attachPrices(newOptions, prices);
      const optionsWithPricesAndStock = attachStock(optionsWithPrices, stocks);

      const sortedOptionsWithPricesAndStock = sortByPriceWithinGroupTypes(
        optionsWithPricesAndStock,
      );

      setOptions(sortedOptionsWithPricesAndStock);
      setSubGroups(newSubgroups);

      const newBaseOptions = optionsWithPricesAndStock.filter(
        (option) => newBaseSelections[option.group_type] === option.variant_id,
      );
      setBaseOptions(newBaseOptions);
      setCurrentOptions(newBaseOptions);
    }
  }, [pcModel]);

  const validatePCIESlots = (_selections, options) => {
    const motherboard = options.find(
      (option) =>
        option.group_type === 'motherboard' &&
        option.variant_id === _selections.motherboard,
    );
    if (!motherboard) {
      return [];
    }

    const pcieTag = motherboard.tags
      .flat()
      .find((tag) => tag.name === 'pcie_slots');
    if (!pcieTag) {
      return [];
    }

    const availableSlots = parseInt(pcieTag.value, 10);
    const usedSlots = calculateUsedPCIESlots(options, _selections);

    if (usedSlots > availableSlots) {
      return [
        {
          attr: 'pcie_slots',
          operator: 'exceed',
          is_aggr: true,
          option: motherboard,
          target: null,
          value: availableSlots,
          message:
            'The number of PCIE Slots of you motherboard has been exceeded, please select another motherboard or remove/change the component',
        },
      ];
    }

    return [];
  };

  const validateSelectedOptions = (_selections) => {
    const body = JSON.stringify(_selections);
    setIsValidating(true);

    return fetch(`/api/pc-models/${slug}/validate`, {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body,
    })
      .then((response) => response.json())
      .then((data) => {
        const apiErrors = data.errors.filter(
          (error) =>
            error.type === 'requirement' && error.attr !== 'pcie_slots',
        );
        const pcieErrors = validatePCIESlots(_selections, options, pcModel);
        const allErrors = [...apiErrors, ...pcieErrors];
        setErrors(allErrors);
        return allErrors; // Returning errors for further use
      })
      .finally(() => setIsValidating(false));
  };

  // Wrapper for calling validateSelectedOptions with debounce
  const debouncedValidateSelectedOptions = debounce((selections, callback) => {
    validateSelectedOptions(selections).then(callback);
  }, 1500);

  useEffect(() => {
    if (!isEmpty(options)) {
      const newCurrentSelections = mergeAll([baseSelections, selections]);
      setCurrentSelections(newCurrentSelections);

      debouncedValidateSelectedOptions(newCurrentSelections, (errors) => {
        setErrors(errors);
      });

      const newCurrentOptions = options.filter(
        (option) =>
          newCurrentSelections[option.group_type] === option.variant_id,
      );

      if (newCurrentSelections.cpu_cooling_system_print) {
        const printOption = options.find(
          (option) =>
            option.variant_id === newCurrentSelections.cpu_cooling_system_print,
        );

        if (printOption) {
          const cpuCoolingIndex = newCurrentOptions.findIndex(
            (option) => option.group_type === 'cpu_cooling_system',
          );

          if (cpuCoolingIndex !== -1) {
            newCurrentOptions.splice(cpuCoolingIndex + 1, 0, {
              ...printOption,
              group_title: 'CPU Cooling System Print',
              group_type: 'cpu_cooling_system_print',
            });
          } else {
            newCurrentOptions.push({
              ...printOption,
              group_title: 'CPU Cooling System Print',
              group_type: 'cpu_cooling_system_print',
            });
          }
        }
      }

      setCurrentOptions(newCurrentOptions);
    }
  }, [options, selections]);

  const breadcrumbs = pcModel?.breadcrumbs ?? [];

  const attrTags = buildPcModelAttrs(currentOptions);

  const sortingOptions = {
    sortInSubGroupOptions: (a, b) => a?.price - b?.price,
  };

  return (
    <PcConfiguratorContext.Provider
      value={{
        pcModel,
        breadcrumbs,
        options,
        baseSelections,
        baseOptions,
        currentSelections,
        currentOptions,
        attrTags,
        subGroups,
        errors,
        isValidating,
        pcConfigDoc,
        sortingOptions,
      }}
    >
      {children}
    </PcConfiguratorContext.Provider>
  );
};

PcConfiguratorProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  prices: PropTypes.arrayOf(
    PropTypes.shape({
      variant_id: PropTypes.number,
      pc_model_uid: PropTypes.string.isRequired,
      group_type: PropTypes.string.isRequired,
      sku: PropTypes.string,
      price_diff: PropTypes.number.isRequired,
      compared_at_price_diff: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  stocks: PropTypes.arrayOf(
    PropTypes.shape({
      variant_id: PropTypes.number,
      quantity: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
};

PcConfiguratorProvider.defaultProps = {};

export const usePcConfigurator = () => useContext(PcConfiguratorContext);
